import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import { Clock, X, Sparkles } from "lucide-react";

interface TrialInfo {
  is_trial: boolean;
  trial_ends_at: string | null;
  days_remaining: number;
}

/**
 * TrialBanner - Displays trial status for solo users
 *
 * Shows:
 * - Days remaining in trial
 * - Upgrade CTA
 * - Dismissible (but re-appears after 24 hours)
 */
export function TrialBanner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSolo } = useRole();
  const [dismissed, setDismissed] = useState(false);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);

  // Check if banner was recently dismissed
  useEffect(() => {
    const dismissedAt = localStorage.getItem("trial_banner_dismissed");
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const hoursSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60);
      // Re-show after 24 hours
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
      } else {
        localStorage.removeItem("trial_banner_dismissed");
      }
    }
  }, []);

  // Fetch trial info from user data or API
  useEffect(() => {
    // For now, use mock data based on user metadata
    // In production, this would come from the API
    if (user && isSolo) {
      // Check if user has trial info in their metadata
      const userTrialInfo = (user as { trial_ends_at?: string; is_trial?: boolean });

      if (userTrialInfo.trial_ends_at) {
        const trialEndDate = new Date(userTrialInfo.trial_ends_at);
        const now = new Date();
        const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        setTrialInfo({
          is_trial: userTrialInfo.is_trial ?? true,
          trial_ends_at: userTrialInfo.trial_ends_at,
          days_remaining: Math.max(0, daysRemaining),
        });
      } else {
        // Default: assume 14-day trial starting now if no info available
        // This is a fallback for new users
        setTrialInfo({
          is_trial: true,
          trial_ends_at: null,
          days_remaining: 14,
        });
      }
    }
  }, [user, isSolo]);

  const handleDismiss = () => {
    localStorage.setItem("trial_banner_dismissed", new Date().toISOString());
    setDismissed(true);
  };

  // Don't show if not solo user, not on trial, or dismissed
  if (!isSolo || !trialInfo?.is_trial || dismissed || trialInfo.days_remaining <= 0) {
    return null;
  }

  // Determine urgency colors based on days remaining
  const isUrgent = trialInfo.days_remaining <= 3;
  const isWarning = trialInfo.days_remaining <= 7 && !isUrgent;

  const bannerClasses = isUrgent
    ? "bg-red-900/30 border-red-800/50"
    : isWarning
      ? "bg-yellow-900/30 border-yellow-800/50"
      : "bg-blue-900/30 border-blue-800/50";

  const textClasses = isUrgent
    ? "text-red-400"
    : isWarning
      ? "text-yellow-400"
      : "text-blue-400";

  return (
    <div
      className={`relative rounded-lg border p-4 ${bannerClasses}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`flex-shrink-0 ${textClasses}`}>
            {isUrgent ? (
              <Clock className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-100">
              {isUrgent ? (
                <>
                  <span className="text-red-400">Only {trialInfo.days_remaining} day{trialInfo.days_remaining !== 1 ? "s" : ""} left</span>
                  {" "}in your free trial
                </>
              ) : (
                <>
                  You have{" "}
                  <span className={textClasses}>
                    {trialInfo.days_remaining} day{trialInfo.days_remaining !== 1 ? "s" : ""}
                  </span>
                  {" "}remaining in your free trial
                </>
              )}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Upgrade now to continue using AIIA without interruption
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/solo/billing")}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
          >
            Upgrade Now
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 text-slate-400 hover:text-slate-300 transition-colors rounded"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
